// ============================================================
// API Client — axios con token Keycloak automático
// ============================================================
import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import Keycloak from "keycloak-js";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8000/api/v1";

let keycloakInstance: Keycloak | null = null;

export function setKeycloakInstance(kc: Keycloak) {
  keycloakInstance = kc;
}

const api: AxiosInstance = axios.create({ 
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

// Interceptor: inyecta token Keycloak en cada petición
api.interceptors.request.use(async (config) => {
  if (keycloakInstance?.authenticated) {
    try {
      await keycloakInstance.updateToken(30); // refresh si expira en < 30s
      config.headers.Authorization = `Bearer ${keycloakInstance.token}`;
    } catch {
      keycloakInstance.login();
    }
  }
  return config;
});

// Interceptor: manejo global de errores
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      keycloakInstance?.login();
    }
    return Promise.reject(error);
  }
);

export default api;

// ── Módulos de API ────────────────────────────────────────────────────────────

export const solicitudesApi = {
  listar: (params?: Record<string, unknown>) =>
    api.get("/solicitudes", { params }),
  obtener: (id: string) => api.get(`/solicitudes/${id}`),
  crear: (data: unknown) => api.post("/solicitudes", data),
  actualizar: (id: string, data: unknown) => api.patch(`/solicitudes/${id}`, data),
  crearProyecto: (id: string) => api.post(`/solicitudes/${id}/crear-proyecto`),
};

export const proyectosApi = {
  listar: (params?: Record<string, unknown>) =>
    api.get("/proyectos", { params }),
  obtener: (id: string) => api.get(`/proyectos/${id}`),
  crear: (data: unknown) => api.post("/proyectos", data),
  actualizar: (id: string, data: unknown) => api.patch(`/proyectos/${id}`, data),
  upsertBC: (id: string, data: unknown) =>
    api.put(`/proyectos/${id}/business-case`, data),
};

export const quickWinsApi = {
  listar: (params?: Record<string, unknown>) =>
    api.get("/quick-wins", { params }),
  crear: (data: unknown) => api.post("/quick-wins", data),
  cerrar: (id: string, esfuerzo: number) =>
    api.patch(`/quick-wins/${id}/cerrar`, null, { params: { esfuerzo_real_dias: esfuerzo } }),
};

export const oportunidadesApi = {
  listar: (params?: Record<string, unknown>) =>
    api.get("/oportunidades", { params }),
  crear: (data: unknown) => api.post("/oportunidades", data),
  crearProyecto: (id: string) =>
    api.post(`/oportunidades/${id}/crear-proyecto`),
};

export const tareasApi = {
  misTareas: () => api.get("/tareas/mis-tareas"),
  vencidas: () => api.get("/tareas/vencidas"),
};

export const reportingApi = {
  dashboard: (dominioId?: string) =>
    api.get("/reporting/dashboard", { params: { dominio_id: dominioId } }),
  semanal: (semanaInicio?: string, dominioId?: string) =>
    api.get("/reporting/semanal", {
      params: { semana_inicio: semanaInicio, dominio_id: dominioId },
    }),
  semanalPdf: (semanaInicio?: string) =>
    api.get("/reporting/semanal/pdf", {
      params: { semana_inicio: semanaInicio },
      responseType: "blob",
    }),
};

export const importacionApi = {
  preview: (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api.post("/importacion/planner/preview", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  confirmar: (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api.post("/importacion/planner/confirmar", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  logs: () => api.get("/importacion/logs"),
};

export const adminApi = {
  usuarios: () => api.get("/admin/usuarios"),
  dominios: () => api.get("/admin/dominios"),
  seedDominios: () => api.post("/admin/seed-dominios"),
};
